/**
 * VideoCoordinator.tsx — Single-play context for blog post pages
 *
 * Provides shared state so only one YouTube embed plays at a time.
 * When a user clicks a second embed, the first embed's iframe unmounts
 * (YouTube playback stops), and the new embed activates.
 *
 * Also used by the floating mini-player (Deliverable C) — it reads
 * activeVideoId from this context to know which video to show in the
 * fixed-position PIP container.
 *
 * Architecture:
 *   - VideoCoordinatorProvider wraps BlogPostPageClient JSX (blog post level only).
 *   - Each YouTubeEmbed reads activeVideoId and calls setActiveVideo on click.
 *   - The FloatingMiniPlayer component reads activeVideoId and inlineRef from context.
 *
 * Usage:
 *   <VideoCoordinatorProvider>
 *     {children}
 *   </VideoCoordinatorProvider>
 *
 * Scope: blog post page only. Not sitewide. Not used on About or other pages.
 */

'use client';

import { createContext, useContext, useState, useCallback, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface VideoCoordinatorContextValue {
  /**
   * The videoId currently playing, or null if nothing is playing.
   * Each YouTubeEmbed renders an iframe when activeVideoId === its own videoId
   * AND isFloating is false.
   * All other embeds render as facade.
   */
  activeVideoId: string | null;

  /**
   * Call this when the user clicks a video's facade. Sets that video as active.
   * Previously active video's iframe unmounts automatically (React state change).
   */
  setActiveVideo: (videoId: string | null) => void;

  /**
   * True when the active video's inline embed has scrolled out of viewport and
   * the FloatingMiniPlayer is showing the iframe in the fixed-position container.
   *
   * When isFloating is true, YouTubeEmbed renders facade (not iframe) for the active
   * video — the FloatingMiniPlayer owns the iframe. This prevents two iframes for the
   * same video from existing simultaneously.
   */
  isFloating: boolean;

  /**
   * Called by FloatingMiniPlayer to inform context that floating state changed.
   */
  setIsFloating: (floating: boolean) => void;

  /**
   * The inline wrapper ref of the currently-active YouTubeEmbed.
   * The FloatingMiniPlayer attaches an IntersectionObserver to this element
   * to know when the inline embed scrolls out of view.
   *
   * Each YouTubeEmbed registers its ref here when it becomes active.
   * Cleared to null when activeVideoId is set to null (close/stop).
   */
  activeInlineRef: RefObject<HTMLDivElement | null>;

  /**
   * Called by the active YouTubeEmbed to register its wrapper ref.
   * Cleared automatically when activeVideoId changes.
   */
  setActiveInlineRef: (ref: HTMLDivElement | null) => void;
}

export const VideoCoordinatorContext = createContext<VideoCoordinatorContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function VideoCoordinatorProvider({ children }: { children: ReactNode }) {
  const [activeVideoId, setActiveVideoIdState] = useState<string | null>(null);
  const [isFloating, setIsFloatingState] = useState(false);
  const activeInlineRef = useRef<HTMLDivElement | null>(null);

  const setActiveVideo = useCallback((videoId: string | null) => {
    setActiveVideoIdState(videoId);
    if (videoId === null) {
      activeInlineRef.current = null;
      setIsFloatingState(false);
    }
  }, []);

  const setIsFloating = useCallback((floating: boolean) => {
    setIsFloatingState(floating);
  }, []);

  const setActiveInlineRef = useCallback((ref: HTMLDivElement | null) => {
    activeInlineRef.current = ref;
  }, []);

  return (
    <VideoCoordinatorContext.Provider value={{ activeVideoId, setActiveVideo, isFloating, setIsFloating, activeInlineRef, setActiveInlineRef }}>
      {children}
    </VideoCoordinatorContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useVideoCoordinator(): VideoCoordinatorContextValue {
  const ctx = useContext(VideoCoordinatorContext);
  if (!ctx) {
    throw new Error('useVideoCoordinator must be used inside <VideoCoordinatorProvider>');
  }
  return ctx;
}
